import {
  chicagoBearsThemeConfig,
  type ModeAwareThemeSource
} from "./themes/chicago-bears-theme.js";

export const shellThemeStorageKey = "kn-shell-theme";
export const customThemeStorageKey = "kn-shell-theme-custom-config";
export const defaultThemeId = "two-circles";

export const themeTokenAliases = {
  "color.action": "--tc-color-brand-fill-loud",
  "color.backdrop": "--tc-color-overlay-modal",
  "color.error": "--tc-color-danger",
  "color.success": "--tc-color-success",
  "color.surface": "--tc-color-surface-default",
  "color.text": "--tc-color-text-normal",
  "color.warning": "--tc-color-warning",
  "sidebar.surface": "--tc-color-surface-lowered",
  "sidebar.surfaceHover": "--tc-color-neutral-fill-quiet",
  "sidebar.text": "--tc-color-text-normal",
  "sidebar.textMuted": "--tc-color-text-quiet"
} as const;

export type ThemeTokenAlias = keyof typeof themeTokenAliases;

export interface ThemeConfig {
  id: string;
  label: string;
  description?: string;
  tokens: Partial<Record<ThemeTokenAlias, string>>;
}

export type ThemeConfigSummary = Pick<ThemeConfig, "id" | "label" | "description">;

interface StoredThemeLibrary {
  customThemes: ThemeConfig[];
  overrides: ThemeConfig[];
}

const tokenAliasSet = new Set<string>(Object.keys(themeTokenAliases));
const darkThemeIds = new Set(["dark"]);
const builtInThemeConfigs = [
  {
    id: defaultThemeId,
    label: "Two Circles",
    description: "Default KoreNext shell brand theme.",
    tokens: {}
  },
  {
    id: "light",
    label: "Light",
    description: "Light color scheme.",
    tokens: {}
  },
  {
    id: "dark",
    label: "Dark",
    description: "Dark color scheme.",
    tokens: {}
  },
  chicagoBearsThemeConfig
] as const satisfies ReadonlyArray<ThemeConfig | ModeAwareThemeSource>;
const builtInThemeIds = new Set<string>(builtInThemeConfigs.map((theme) => theme.id));

export function getAvailableThemeConfigs(): ThemeConfigSummary[] {
  const library = readStoredThemeLibrary();
  const summaries = builtInThemeConfigs.map((theme) =>
    summarizeThemeConfig(resolveBuiltInThemeConfig(theme, library.overrides, getCurrentShellMode()))
  );

  return [...summaries, ...library.customThemes.map(summarizeThemeConfig)];
}

export function loadThemeConfigFromJson(json: string): ThemeConfig {
  let parsed: unknown;

  try {
    parsed = JSON.parse(json);
  } catch {
    throw new Error("Theme config must be valid JSON.");
  }

  return validateThemeConfig(parsed);
}

export function applyThemeConfig(
  config: ThemeConfig,
  target: HTMLElement = document.documentElement
): void {
  clearRuntimeThemeConfig(target);
  let colorScheme = getCurrentShellMode(target);
  if (config.id === "dark") {
    colorScheme = "dark";
  } else if (config.id === "light" || config.id === defaultThemeId) {
    colorScheme = "light";
  }
  target.dataset.tcColorScheme = colorScheme;

  for (const [alias, value] of Object.entries(config.tokens)) {
    target.style.setProperty(themeTokenAliases[alias as ThemeTokenAlias], value);
  }
}

export function clearRuntimeThemeConfig(target: HTMLElement = document.documentElement): void {
  for (const cssProperty of Object.values(themeTokenAliases)) {
    target.style.removeProperty(cssProperty);
  }
}

export function persistThemeConfig(config: ThemeConfig): void {
  const library = readStoredThemeLibrary();

  if (config.id !== defaultThemeId && builtInThemeIds.has(config.id)) {
    // For built-in themes, only store an empty override if no tokens are defined
    // If tokens are explicitly set (custom edits), store the full config
    const override = Object.keys(config.tokens).length === 0
      ? {
          id: config.id,
          label: config.label,
          ...(config.description ? { description: config.description } : {}),
          tokens: {}
        }
      : config;
    writeStoredThemeLibrary({
      ...library,
      overrides: upsertThemeConfig(library.overrides, override)
    });
  } else if (!builtInThemeIds.has(config.id)) {
    writeStoredThemeLibrary({
      ...library,
      customThemes: upsertThemeConfig(library.customThemes, config)
    });
  }

  persistActiveThemeId(config.id);
}

export function persistActiveThemeId(themeId: string): void {
  localStorage.setItem(shellThemeStorageKey, themeId);
}

export function getStoredThemeConfig(): ThemeConfig {
  return resolveThemeConfig(localStorage.getItem(shellThemeStorageKey), getCurrentShellMode());
}

export function resolveThemeConfig(themeId: string | null, shellMode: string = getCurrentShellMode()): ThemeConfig {
  const library = readStoredThemeLibrary();
  const builtIn = builtInThemeConfigs.find((theme) => theme.id === themeId);
  if (builtIn) {
    return resolveBuiltInThemeConfig(builtIn, library.overrides, shellMode);
  }

  const customConfig = library.customThemes.find((theme) => theme.id === themeId);
  if (customConfig) {
    return cloneThemeConfig(customConfig);
  }

  return materializeBuiltInThemeConfig(builtInThemeConfigs[0], shellMode);
}

function validateThemeConfig(value: unknown): ThemeConfig {
  if (!isRecord(value)) {
    throw new Error("Theme config must be a JSON object.");
  }

  const { description, id, label, tokens } = value;

  if (typeof id !== "string" || id.trim() === "") {
    throw new Error("Theme config id must be a non-empty string.");
  }

  if (typeof label !== "string" || label.trim() === "") {
    throw new Error("Theme config label must be a non-empty string.");
  }

  if (description !== undefined && typeof description !== "string") {
    throw new Error("Theme config description must be a string when provided.");
  }

  if (!isRecord(tokens)) {
    throw new Error("Theme config tokens must be an object.");
  }

  const entries = Object.entries(tokens);
  // Allow empty tokens for theme selection without customization
  // (full validation only applies when tokens are provided)

  const validatedTokens: Partial<Record<ThemeTokenAlias, string>> = {};

  for (const [alias, tokenValue] of entries) {
    if (!tokenAliasSet.has(alias)) {
      throw new Error(`Unknown theme token alias: ${alias}`);
    }

    if (typeof tokenValue !== "string" || tokenValue.trim() === "") {
      throw new Error(`Theme token ${alias} must be a non-empty string.`);
    }

    validatedTokens[alias as ThemeTokenAlias] = tokenValue;
  }

  return {
    id: id.trim(),
    label: label.trim(),
    ...(typeof description === "string" && description.trim() !== ""
      ? { description: description.trim() }
      : {}),
    tokens: validatedTokens
  };
}

function readStoredThemeLibrary(): StoredThemeLibrary {
  const storedConfig = localStorage.getItem(customThemeStorageKey);

  if (!storedConfig) {
    return {
      customThemes: [],
      overrides: []
    };
  }

  try {
    const parsed = JSON.parse(storedConfig) as unknown;

    if (isThemeLibraryRecord(parsed)) {
      return {
        customThemes: parseStoredThemeList(parsed.customThemes),
        overrides: parseStoredThemeList(parsed.overrides)
      };
    }

    return {
      customThemes: [validateThemeConfig(parsed)],
      overrides: []
    };
  } catch {
    return {
      customThemes: [],
      overrides: []
    };
  }
}

function summarizeThemeConfig(config: ThemeConfig): ThemeConfigSummary {
  return {
    id: config.id,
    label: config.label,
    description: config.description
  };
}

function cloneThemeConfig(config: ThemeConfig): ThemeConfig {
  return {
    ...config,
    tokens: { ...config.tokens }
  };
}

function resolveBuiltInThemeConfig(
  builtInTheme: ThemeConfig | ModeAwareThemeSource,
  overrides: ThemeConfig[],
  shellMode: string
): ThemeConfig {
  const materializedBuiltIn = materializeBuiltInThemeConfig(builtInTheme, shellMode);

  if (builtInTheme.id === defaultThemeId) {
    return cloneThemeConfig(materializedBuiltIn);
  }

  const override = overrides.find((theme) => theme.id === builtInTheme.id);

  if (!override) {
    return cloneThemeConfig(materializedBuiltIn);
  }

  return {
    ...cloneThemeConfig(materializedBuiltIn),
    ...(override.description ? { description: override.description } : {}),
    tokens: { ...override.tokens }
  };
}

function upsertThemeConfig(configs: ThemeConfig[], config: ThemeConfig): ThemeConfig[] {
  const nextConfig = cloneThemeConfig(config);
  const existingIndex = configs.findIndex((entry) => entry.id === config.id);

  if (existingIndex === -1) {
    return [...configs, nextConfig];
  }

  return configs.map((entry, index) => (index === existingIndex ? nextConfig : entry));
}

function writeStoredThemeLibrary(library: StoredThemeLibrary): void {
  localStorage.setItem(customThemeStorageKey, JSON.stringify({
    customThemes: library.customThemes,
    overrides: library.overrides
  }));
}

function parseStoredThemeList(value: unknown): ThemeConfig[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((entry) => {
    try {
      return [validateThemeConfig(entry)];
    } catch {
      return [];
    }
  });
}

function isThemeLibraryRecord(value: unknown): value is {
  customThemes?: unknown;
  overrides?: unknown;
} {
  return isRecord(value) && ("customThemes" in value || "overrides" in value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function getCurrentShellMode(target?: HTMLElement): string {
  if (typeof document === "undefined") {
    return "light";
  }

  return (target ?? document.documentElement).dataset.tcColorScheme ?? "light";
}

function isModeAwareThemeConfig(value: ThemeConfig | ModeAwareThemeSource): value is ModeAwareThemeSource {
  return "lightTokens" in value && "darkTokens" in value;
}

function materializeBuiltInThemeConfig(
  theme: ThemeConfig | ModeAwareThemeSource,
  shellMode: string
): ThemeConfig {
  if (!isModeAwareThemeConfig(theme)) {
    return cloneThemeConfig(theme);
  }

  return validateThemeConfig({
    id: theme.id,
    label: theme.label,
    description: theme.description,
    tokens: darkThemeIds.has(shellMode) ? theme.darkTokens : theme.lightTokens
  });
}
